import { registerWidget, type CellContentProps } from '../registry'
import { optionColor } from '../optionColor'
import { slugToWords } from '../OptionLabel'
import { GroupedChips } from '../GroupedChips'
import { groupValues, serverGroups } from '../valueGroups'

interface Option { id: string; label?: string; color?: string }

function EnumSetContent({ value, config }: CellContentProps) {
  const options: Option[] = Array.isArray((config as Record<string,unknown>)?.['options'])
    ? ((config as Record<string,unknown>)['options'] as Option[])
    : []
  const byId = new Map(options.map((o) => [o.id, o]))
  // SEN-205: the one set layout — identical option KEYS group (never labels)
  // with exact counts; every option is shown, compacting instead of hiding.
  // A path set (server groups of whole enum-set values) weighs each member
  // by its group's exact count.
  const fromServer = serverGroups(value)
  const members: { id: string; weight: number }[] = fromServer
    ? fromServer.flatMap((g) => (Array.isArray(g.value) ? g.value : [g.value]).map((id) => ({ id: String(id), weight: g.count })))
    : (Array.isArray(value) ? value : []).map((id) => ({ id: String(id), weight: 1 }))
  const groups = groupValues(members, (m) => m.id, (m) => byId.get(m.id)?.label ?? slugToWords(m.id), (m) => m.weight)

  // Each chip carries its OPTION's colour (owner decision 2026-09-24), keyed by
  // the stable id — never the label — the same bullet a select value shows.
  return (
    <GroupedChips
      groups={groups}
      renderGroup={(group, badge, compact) => (
        <span
          data-option={group.key}
          className="inline-flex items-center gap-1 rounded-sm bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground"
        >
          <span
            data-testid="option-bullet"
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: optionColor(group.key, options) }}
          />
          <span data-testid="set-group-label" className={compact ? 'sr-only' : ''}>{group.label}</span>
          {badge}
        </span>
      )}
    />
  )
}

registerWidget('enum-set', { read: EnumSetContent, defaultWidth: 200, readsSets: true })
